import { Pricings, EventDescription } from './types/event'
import {
  getPricingPricePerPerson,
  getPricingBasePrice,
  getPricingTotalPrice,
  getPricingAppliedDiscount,
} from '~/utils/pricingGetters'
import { eventGetters } from '~/utils/eventGetters'

export const useGetPricingKey = ({
  dateId = ref(undefined),
  quantity,
  event,
}: {
  dateId: Ref<string | undefined>
  quantity: Ref<number>
  event: Ref<EventDescription | undefined>
}) => {
  const isSpecialGiftcard = computed(() =>
    eventGetters.isGiftcardEvent(event.value)
  )
  const isBoxEvent = computed(() => eventGetters.isBoxEvent(event.value))

  // Check if we should use single pricing (box events or special giftcard events)
  const shouldUseSinglePricing = computed(
    () => isBoxEvent.value || isSpecialGiftcard.value
  )

  const pricingKey = computed(() => {
    if (shouldUseSinglePricing.value) {
      return `${quantity.value}`
    }

    return `${quantity.value}-${dateId.value}`
  })

  return {
    shouldUseSinglePricing,
    pricingKey,
  }
}

export const useEventPricingsGetters = (
  pricingsData: Ref<Record<string, Pricings>>,
  {
    dateId = ref(undefined),
    quantity,
    event,
  }: {
    dateId: Ref<string | undefined>
    quantity: Ref<number>
    event: Ref<EventDescription | undefined>
  }
) => {
  const { $i18n } = useNuxtApp()
  const locale = $i18n.locale
  const { shouldUseSinglePricing, pricingKey } = useGetPricingKey({
    dateId,
    quantity,
    event,
  })

  const hasSale = ref(false)

  // Get the pricing object to use
  const pricingData = computed(() => pricingsData.value[pricingKey.value])

  const unitPrice = computed(() => {
    if (!pricingData.value) {
      return undefined
    }

    const firstPriceBreakdownTitle =
      pricingData.value.price_breakdown?.[0]?.title

    return getPricingPricePerPerson(
      pricingData.value,
      firstPriceBreakdownTitle || 'ADULT'
    )
  })

  const basePrice = computed(() => {
    if (!pricingData.value) {
      return undefined
    }
    return getPricingBasePrice(pricingData.value)
  })

  const totalPrice = computed(() => {
    if (!pricingData.value) {
      return undefined
    }

    const price = getPricingTotalPrice(pricingData.value)
    return formatPriceAmount(
      Number(price.amount),
      convertLocaleIntoBCP47(locale.value),
      price.currency
    )
  })

  const appliedDiscount = computed(() => {
    if (!pricingData.value) {
      return undefined
    }
    return getPricingAppliedDiscount(pricingData.value)
  })

  watch(pricingData, () => {
    if (!pricingData.value) {
      // promotions should usually be applide to all pricings,
      // so we only wait for the pricing data to be available before checking for a sale
      // this avoids unnecessary re-renders
      return
    }

    if (appliedDiscount.value && Number(appliedDiscount.value.amount) > 0) {
      hasSale.value = true
    } else {
      hasSale.value = false
    }
  })

  return {
    pricingKey,
    pricingData,
    unitPrice,
    basePrice,
    totalPrice,
    appliedDiscount,
    hasSale,
    shouldUseSinglePricing,
  }
}
